import type { Contract, ValueProcessor } from 'validata';
import {
  asDate,
  isArray,
  isEnum,
  isObject,
  isString,
  maybeBoolean,
  maybeDate,
  maybeObject,
  maybeString,
  StringFormat,
} from 'validata';
import { isObjectSet } from 'validata-mongo';
import type { Client } from './client.js';
import type { RequestContext } from './request-context.js';

export namespace Person {
  export const NAME = 'person' as const;

  /**
   * The roles an organization can grant. Super admin is deliberately absent:
   * it is a property of the person, not an organization role, and so cannot be
   * granted by an organization admin editing roles.
   */
  export const ROLE = {
    admin: 'admin',
    patroller: 'patroller',
    'summary-recipient': 'summary-recipient',
    watcher: 'watcher',
  } as const;

  export type Role = (typeof ROLE)[keyof typeof ROLE];

  export const isRole = (): ValueProcessor<Role> => isEnum<Role>(ROLE);

  interface AuthenticationFailure extends Client {
    when: Date;
  }

  interface PasswordReset {
    created: RequestContext;
    expires?: Date;
    key: string;
  }

  export interface Appearance {
    colorMode?: string;
    largeText?: boolean;
  }

  /** A person's standing in one organization. Roles are per-organization. */
  export interface Membership {
    finished?: Date;
    roles: Role[];
    started: Date;
  }

  /**
   * A person as stored. One person spans every organization they belong to, so
   * `organizations` - not a single `organizationId` - says where they patrol.
   *
   * Responses use `View`, which narrows this to one organization.
   */
  export interface Type {
    _id: string;
    appearance?: Appearance;
    authenticationFailures?: AuthenticationFailure[];
    created: RequestContext;
    /** Account-level suspension. Per-organization standing is `Membership`. */
    disabled?: RequestContext;
    email: string;
    firstName: string;
    lastName: string;
    /** The organization to resume on the next sign in. */
    lastOrganizationId?: string;
    lockedOut?: RequestContext;
    organizations: Record<string, Membership>;
    password?:
      | {
          digest: string;
          reset?: PasswordReset;
          salt: string;
          set: RequestContext;
        }
      | {
          reset: PasswordReset;
        };
    phone?: string;
    /**
     * Never settable through any contract - see `isSuperAdminPerson` in the
     * api package. Super admins hold no memberships.
     */
    superAdmin?: boolean;
    updated: RequestContext;
  }

  /**
   * A person as seen from inside one organization: their membership flattened
   * onto the person, and every other organization omitted.
   */
  export interface View
    extends Omit<
      Type,
      | 'authenticationFailures'
      | 'lastOrganizationId'
      | 'organizations'
      | 'password'
      | 'superAdmin'
    > {
    finished?: Date;
    organizationId: string;
    roles: Role[];
    started: Date;
  }

  /**
   * A person with no organization in view: their account, stripped of
   * credentials and of which organizations they belong to. Used for a super
   * admin, whose session has no organization.
   */
  export interface AccountView
    extends Omit<
      Type,
      'authenticationFailures' | 'organizations' | 'password'
    > {}

  export const toAccountView = (person: Type): AccountView => {
    const {
      authenticationFailures: _authenticationFailures,
      organizations: _organizations,
      password: _password,
      ...rest
    } = person;

    return rest;
  };

  /** Narrows a stored person to one organization. Returns undefined when they do not belong to it. */
  export const toView = (
    person: Type,
    organizationId: string,
  ): View | undefined => {
    const membership = person.organizations?.[organizationId];
    if (!membership) return undefined;

    const {
      authenticationFailures: _authenticationFailures,
      lastOrganizationId: _lastOrganizationId,
      organizations: _organizations,
      password: _password,
      superAdmin: _superAdmin,
      ...rest
    } = person;

    return {
      ...rest,
      finished: membership.finished,
      organizationId,
      roles: membership.roles,
      started: membership.started,
    };
  };

  export const membership = (
    person: Pick<Type, 'organizations'>,
    organizationId: string | undefined,
  ): Membership | undefined =>
    organizationId ? person.organizations?.[organizationId] : undefined;

  export const rolesIn = (
    person: Pick<Type, 'organizations'>,
    organizationId: string | undefined,
  ): Role[] => membership(person, organizationId)?.roles ?? [];

  /** Whether the person may act in - and sign in to - this organization. */
  export const isActiveIn = (
    person: Pick<Type, 'organizations'>,
    organizationId: string | undefined,
  ): boolean => {
    const found = membership(person, organizationId);
    return !!found && !found.finished;
  };

  /** The organizations the person may select, most recently started first. */
  export const activeOrganizationIds = (
    person: Pick<Type, 'organizations'>,
  ): string[] =>
    Object.entries(person.organizations ?? {})
      .filter(([, value]) => !value.finished)
      .sort(
        ([, a], [, b]) =>
          new Date(b.started).getTime() - new Date(a.started).getTime(),
      )
      .map(([organizationId]) => organizationId);

  /**
   * The organization a session should open in: the preferred one when it is
   * still selectable, otherwise the most recently started.
   */
  export const resolveOrganizationId = (
    person: Pick<Type, 'organizations'>,
    preferred?: string,
  ): string | undefined => {
    if (isActiveIn(person, preferred)) return preferred;
    return activeOrganizationIds(person)[0];
  };

  /** One selectable organization, for the switcher. */
  export interface MyOrganization {
    _id: string;
    code: string;
    current: boolean;
    name: string;
    roles: Role[];
    started: Date;
  }

  /**
   * The organization to brand an email to this person with: the one they would
   * sign in to, else any they have belonged to. Undefined only for someone with
   * no memberships at all (a super admin).
   */
  export const brandingOrganizationId = (
    person: Pick<Type, 'lastOrganizationId' | 'organizations'>,
  ): string | undefined =>
    resolveOrganizationId(person, person.lastOrganizationId) ??
    Object.keys(person.organizations ?? {})[0];

  export interface PatrolSummary {
    allTime: number;
    inLastQuarter: number;
    inLastYear: number;
  }

  export type Create = Pick<
    View,
    'firstName' | 'lastName' | 'email' | 'phone' | 'roles'
  >;
  export type Patch = Pick<
    View,
    'firstName' | 'lastName' | 'email' | 'phone' | 'roles'
  >;

  export const createContract: Contract<Create> = {
    email: isString({ format: StringFormat.email() }),
    firstName: isString({ trim: 'both' }),
    lastName: isString({ trim: 'both' }),
    phone: maybeString({ trim: 'both' }),
    roles: isArray(isRole()),
  };

  export const create = isObject<Create>(createContract);

  export const patchContract: Contract<Patch> = {
    email: isString({ format: StringFormat.email() }),
    firstName: isString({ trim: 'both' }),
    lastName: isString({ trim: 'both' }),
    phone: maybeString({ trim: 'both' }),
    roles: isArray(isRole()),
  };

  export const patch = isObjectSet<Patch>(patchContract);

  export const membershipContract: Contract<Membership> = {
    finished: maybeDate(),
    roles: isArray(isRole()),
    started: asDate(),
  };

  export interface MyProfilePatch {
    appearance?: Appearance;
  }

  export const appearanceContract: Contract<Appearance> = {
    colorMode: maybeString({
      regex: /^(dark|light)$/,
      trim: 'both',
    }),
    largeText: maybeBoolean(),
  };

  export const myProfilePatchContract: Contract<MyProfilePatch> = {
    appearance: maybeObject(appearanceContract),
  };

  export const myProfilePatch = isObjectSet<MyProfilePatch>(
    myProfilePatchContract,
  );
}
